import React, {useState} from "react"
import { css } from "styled-components"
import { Link, StaticQuery, graphql } from "gatsby"
import { Box } from "../components/Flex"
import { Override, toRem } from "../utils"
import theme from "../utils/theme"
import styled from "styled-components"
import { useTranslation } from "react-i18next"
import { getCurrentLang } from "../i18n"
import helpCenter from "../img/help-center.svg"
import media from "../utils/media"

export const Title = ({ children, ...props }) => {
  const childStyle = css`
    font-weight: 500;
  `
  return (
    <Box
      pt="8rem"
      pb="1rem"
      textAlign="center"
      width={1}
      color="gray"
      {...props}
    >
      <Override style={childStyle}>{children}</Override>
    </Box>
  )
}

const SideBarContainer = styled(props => (
  <div {...props} data-testid="sidebar" />
))`
  background-color: white;
  border: 1px solid #d6d6d6;
  width: 100%;
  position: sticky;
  top: 1rem;
`

const Item = styled.p`
  color: ${theme.colors.gray};
  font-size: ${toRem(18)};
  margin-bottom: 0;
  padding: ${toRem(12)} ${toRem(27)};
  padding-right: ${toRem(20)};
  border-bottom: 1px solid #d6d6d6;
  cursor: pointer;
  font-weight: 400;
  background-color: ${props => (props.sub ? theme.colors.sideBarGray : null)};
  z-index: 100;
  border-left: 4px solid transparent;
  ${media.fromPhone`
    &:hover {
      background-color: ${props => (!props.selected ? theme.colors.sideBarGray : null)};
      border-left: ${props =>
        !props.selected
          ? `4px solid ${theme.colors.text}`
          : `4px solid transparent`};
    }
  `};

  &:last-child {
    border-bottom: 0;
  }
`

const checked = css`
  max-height: 100vh;
  opacity: 1;
`

const SubItems = styled.div`
  max-height: 0;
  opacity: 0;
  z-index: 0;
  transition: all 0.5s ease 0s;
  visibility: ${props => (!props.checked ? "hidden" : null)};
  ${props => (props.checked ? checked : null)}
`

const UnstyledLink = styled(Link)`
  text-decoration: none;
  color: ${theme.colors.gray};
  display: block;
  font-size: ${toRem(18)};
  margin-bottom: 0;
  padding: ${toRem(12)} ${toRem(27)};
  padding-right: ${toRem(20)};
  border-bottom: 1px solid #d6d6d6;
  cursor: pointer;
  font-weight: 400;
  background-color: ${props => (props.sub ? theme.colors.sideBarGray : null)};
  z-index: 0;
  border-left: ${props =>
    props.selected
      ? `4px solid ${theme.colors.text}`
      : `4px solid transparent`};

  &:last-child {
    border-bottom: 0;
  }
`

const Breadcrumbs = styled(props => (
  <p data-testid="sidebar-breadcrumb" {...props} />
))`
  color: ${theme.colors.gray};
  font-size: 10px;
  margin-bottom: 1rem;
  text-align: right;
  line-height: 10px;
`

const treeify = list => {
  // from
  // {title: "Passengers", subTitle: "Account creation", slug: "/help-center/account-creation/"}
  // to
  // [ { title: "Passengers", items: [{subTitle: "Account creation", slug: "/help-center/account-creation/"}]} ]
  let treeList = []
  const localList = list.slice(0)
  localList.forEach(obj => {
    const parent = treeList.find(item => item.title === obj.title)
    const child = { subTitle: obj.subTitle, slug: obj.slug }
    if (parent) {
      parent.items.push(child)
    } else {
      const newParent = { title: obj.title, items: [child] }
      treeList.push(newParent)
    }
  })
  return treeList
}

const SideBar = ({ groups, elements, path }) => {
  const { t } = useTranslation()
  const [ open, setOpen ] = useState(null)
  let itemsWithTitle = elements
    .filter(item => item.node.frontmatter.language === getCurrentLang())
    .map(({ node: item }) => {
      const slug = item.fields.slug
      const title = item.frontmatter.parent
      const subTitle = item.frontmatter.title
      return {
        title,
        subTitle,
        slug
      }
    })
  let items = treeify(itemsWithTitle)
  items = groups.items.map(item => {
    const groupTitle = items.find(
      itemWithTitle => itemWithTitle.title === item.title
    )
    if (groupTitle) return groupTitle
    else {
      return { title: item.title, items: [] }
    }
  })
  let currentItem = itemsWithTitle.find(item => item.slug === path)
  const breadcrumb =
    currentItem && currentItem.title && currentItem.subTitle
      ? `${t("menu")} / ${currentItem.title} / ${currentItem.subTitle}`
      : `${t("menu")}`


  const onClickItem = title => {
    if (title === open) {
      return setOpen(null)
    }
    setOpen(title)
  }

  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (currentItem && !open) {
      setOpen(currentItem.title)
    } 
  }, [])
  /* eslint-disable react-hooks/exhaustive-deps */

  return (
    <>
      <Breadcrumbs test-id="sidebar-breadcrumb">{breadcrumb}</Breadcrumbs>
      <SideBarContainer>
        {items.map(({ title, items }) => (
          <React.Fragment key={title}>
            <Item key={title} onClick={() => onClickItem(title)} selected={title === open}>
              {title}
            </Item>
            {(
              <SubItems checked={title === open}>
                {items.map(({ subTitle, slug }) => {
                  return (
                    <UnstyledLink
                      to={slug}
                      key={subTitle}
                      selected={slug === path}
                      sub="true"
                    >
                      {subTitle}
                    </UnstyledLink>
                  )
                })}
              </SubItems>
            )}
          </React.Fragment>
        ))}
      </SideBarContainer>
    </>
  )
}

export const Content = styled(props => <Box {...props} />)`
  h3 {
    font-weight: 500 !important;
  }
  p {
    font-size: 18px;
  }
`

const H4 = styled.h4`
  text-align: center;
  font-weight: 400;
  font-size: 25px;
  line-height: 25px;
`

const P = styled.p`
  text-align: center;
  font-weight: 400;
  font-size: 20px;
  line-height: 34px;
  margin-bottom: 4rem;
`

export const HelpCenterIndex = () => {
  const { t } = useTranslation()
  return (
    <>
      <img
        src={helpCenter}
        style={{ display: "block", margin: "auto", paddingBottom: "2rem" }}
        alt="Help Center"
      ></img>
      <H4 style={{ textAlign: "center" }}>{t("cantFind")}</H4>
      <P style={{ textAlign: "center" }}>{t("writeUs")}</P>
    </>
  )
}

export default props => (
  <StaticQuery
    query={graphql`
      query SideBar {
        groups: allMarkdownRemark(
          filter: { frontmatter: { templateKey: { eq: "help-center-groups" } } }
        ) {
          edges {
            node {
              excerpt(pruneLength: 400)
              id
              fields {
                slug
              }
              frontmatter {
                templateKey
                language
                items {
                  title
                }
              }
            }
          }
        }

        elements: allMarkdownRemark(
          sort: { order: ASC, fields: [frontmatter___title] }
          filter: { frontmatter: { templateKey: { eq: "help-center" } } }
        ) {
          edges {
            node {
              excerpt(pruneLength: 400)
              id
              fields {
                slug
              }
              frontmatter {
                templateKey
                title
                language
                parent
                items {
                  title
                  description
                  tags
                }
              }
            }
          }
        }
      }
    `}
    render={(data, count) => {
      let groups
      if (props.groups) {
        groups = props.groups
      } else {
        groups = data.groups.edges.find(
          item => item.node.frontmatter.language === getCurrentLang()
        )
        if(groups){
          groups.items = groups.node.frontmatter.items
        } else {
          groups = { items: [] }
        }
      }
      return (
        <SideBar
          {...props}
          groups={groups}
          elements={data.elements.edges}
          count={count}
        />
      )
    }}
  />
)
